import React from 'react'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Checkbox } from '@/common/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { ChevronDown } from 'lucide-react'

import type { GetBankProviderResponse } from '@/modules/bank/type'

interface BankProviderSelectorProps {
	field: {
		value: string[] | null | undefined
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onChange: (value: any) => void
	}
	bankProviders: GetBankProviderResponse[] | undefined
}

export const BankProviderSelector = ({
	field,
	bankProviders,
}: BankProviderSelectorProps) => {
	const [search, setSearch] = React.useState('')
	const filteredProviders = React.useMemo(() => {
		if (!search) return bankProviders
		return bankProviders?.filter(
			(provider) =>
				provider.name.toLowerCase().includes(search.toLowerCase()) ||
				provider.nameTH.toLowerCase().includes(search.toLowerCase()) ||
				provider.code.toString().includes(search),
		)
	}, [bankProviders, search])

	const value = Array.isArray(field.value) ? field.value : []
	const selectedCount = value.length

	const handleProviderToggle = (providerCode: string) => {
		const isSelected = value.includes(providerCode)
		const newValues = isSelected
			? value.filter((code) => code !== providerCode)
			: [...value, providerCode]

		field.onChange(newValues)
	}

	return (
		<div className="space-y-2">
			<Popover
				onOpenChange={(open) => {
					if (!open) {
						setSearch('')
					}
				}}
			>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						className="w-full justify-between"
					>
						<div className="flex items-center gap-2 truncate">
							<span className="truncate">
								{selectedCount > 0
									? `${selectedCount} ธนาคารที่เลือก`
									: 'เลือกธนาคารที่ต้องการซ่อน'}
							</span>
						</div>
						<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-full p-0" align="start">
					<div className="space-y-2 p-2">
						<Input
							placeholder="ค้นหาธนาคาร..."
							className="h-8 text-sm"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<div className="max-h-[300px] space-y-1 overflow-y-auto">
							{filteredProviders?.map((provider) => (
								<div
									key={provider.id}
									className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent"
									onClick={() => handleProviderToggle(provider.code.toString())}
								>
									<Checkbox
										checked={value.includes(provider.code.toString())}
										className="pointer-events-none h-4 w-4"
									/>
									<span className="text-xs">
										{provider.name} - {provider.nameTH} ({provider.code})
									</span>
								</div>
							))}
							{filteredProviders?.length === 0 && (
								<div className="py-2 text-center text-xs text-muted-foreground">
									ไม่พบธนาคาร
								</div>
							)}
						</div>
						{value.length > 0 && (
							<Button
								variant="ghost"
								className="w-full justify-center text-xs"
								onClick={() => field.onChange([])}
							>
								ล้างทั้งหมด
							</Button>
						)}
					</div>
				</PopoverContent>
			</Popover>
		</div>
	)
}
